import { useMemo } from "react";

// Routing filters for one endpoint (TZ §2.3): each attribute is All / Include /
// Exclude over a list of values. Used on BOTH sides — an SSP endpoint filters
// the traffic it accepts, a DSP endpoint filters the traffic it is sent — so the
// same editor serves the publisher form and the transit campaign wizard.
//
// Shape (matches the backend's EndpointFiltersSchema):
//   { country: { mode: "include", values: ["UKR"] }, ... }
// An attribute set to "all", or absent, never restricts.

const TRAFFIC_TYPES = [
  ["1", "Web"],
  ["2", "Mobile Web"],
  ["3", "App"],
  ["4", "CTV"],
  ["5", "OTT"],
];

const AD_FORMATS = [
  ["1", "Video"],
  ["2", "Banner"],
  ["3", "Native"],
  ["4", "Audio"],
];

// OpenRTB 2.5 table 5.21, the values partners actually send.
const DEVICE_TYPES = [
  ["1", "Mobile / Tablet"],
  ["2", "Personal Computer"],
  ["3", "Connected TV"],
  ["4", "Phone"],
  ["5", "Tablet"],
  ["6", "Connected Device"],
  ["7", "Set Top Box"],
];

// Each attribute is either a free list (one value per line) or a fixed choice.
export const FILTER_ATTRIBUTES = [
  { key: "country", label: "Country", hint: "ISO alpha-3, e.g. UKR" },
  { key: "trafficType", label: "Traffic type", choices: TRAFFIC_TYPES },
  { key: "deviceType", label: "Device type", choices: DEVICE_TYPES },
  { key: "os", label: "OS", hint: "e.g. android, ios" },
  { key: "domain", label: "Domain", hint: "Site domain; subdomains match" },
  { key: "bundle", label: "App bundle", hint: "Exact bundle id" },
  { key: "adFormat", label: "Ad format", choices: AD_FORMATS },
  { key: "dealId", label: "Deal ID", hint: "PMP deal ids" },
];

const MODES = [
  ["all", "All"],
  ["include", "Include"],
  ["exclude", "Exclude"],
];

const parseList = (raw) =>
  raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

function AttributeRow({ attribute, filter, onChange }) {
  const mode = filter?.mode ?? "all";
  const values = filter?.values ?? [];
  const active = mode !== "all";

  const setMode = (nextMode) => {
    if (nextMode === "all") {
      onChange(attribute.key, undefined);
      return;
    }
    onChange(attribute.key, { mode: nextMode, values });
  };

  const setValues = (nextValues) =>
    onChange(attribute.key, { mode, values: nextValues });

  const toggleChoice = (choiceValue) =>
    setValues(
      values.includes(choiceValue)
        ? values.filter((v) => v !== choiceValue)
        : [...values, choiceValue],
    );

  return (
    <div className="flex flex-col gap-1.5 border-b border-(--border-subtle) py-2 last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] font-medium text-(--text-secondary)">
          {attribute.label}
        </span>
        <div
          role="radiogroup"
          aria-label={`${attribute.label} filter mode`}
          className="inline-flex rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-0.5"
        >
          {MODES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={mode === value}
              onClick={() => setMode(value)}
              className={`rounded-(--radius-sm) px-2 py-0.5 text-xs transition-colors ${
                mode === value
                  ? "bg-(--surface-muted) font-semibold text-(--text-primary)"
                  : "text-(--text-secondary) hover:text-(--text-primary)"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {active && attribute.choices && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {attribute.choices.map(([choiceValue, choiceLabel]) => (
            <label
              key={choiceValue}
              className="flex cursor-pointer items-center gap-1.5 text-xs text-(--text-secondary)"
            >
              <input
                type="checkbox"
                checked={values.includes(choiceValue)}
                onChange={() => toggleChoice(choiceValue)}
              />
              <span>{choiceLabel}</span>
            </label>
          ))}
        </div>
      )}

      {active && !attribute.choices && (
        <>
          <textarea
            value={values.join("\n")}
            onChange={(e) => setValues(parseList(e.target.value))}
            placeholder="One value per line, or comma-separated"
            rows={2}
          />
          {attribute.hint && <p className="form-help-text">{attribute.hint}</p>}
        </>
      )}

      {active && values.length === 0 && (
        <p className="form-help-text">
          No values yet, so this filter does not restrict anything.
        </p>
      )}
    </div>
  );
}

export default function EndpointFilters({ value, onChange, description }) {
  const filters = value ?? {};
  const activeCount = useMemo(
    () =>
      Object.values(filters).filter(
        (f) => f && f.mode !== "all" && f.values?.length,
      ).length,
    [filters],
  );

  const handleAttributeChange = (key, next) => {
    const updated = { ...filters };
    if (next === undefined) delete updated[key];
    else updated[key] = next;
    onChange(Object.keys(updated).length > 0 ? updated : null);
  };

  return (
    <div className="flex flex-col gap-1">
      {description && <p className="form-help-text">{description}</p>}
      <p className="form-help-text">
        {activeCount === 0
          ? "No filters: every request is eligible."
          : `${activeCount} active filter${activeCount === 1 ? "" : "s"}. A request must pass all of them.`}
      </p>
      <div className="rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) px-3">
        {FILTER_ATTRIBUTES.map((attribute) => (
          <AttributeRow
            key={attribute.key}
            attribute={attribute}
            filter={filters[attribute.key]}
            onChange={handleAttributeChange}
          />
        ))}
      </div>
    </div>
  );
}
